import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/auth';
import {
  ArrowLeft, Save, Pause, Play, StopCircle,
  Phone, MessageCircle, Calendar, TrendingUp,
  ExternalLink, Mail, Building, User, DollarSign,
  CalendarDays, BarChart3
} from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

interface ClientData {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  contract_type: string | null;
  monthly_price: number | null;
  start_date: string | null;
  end_date: string | null;
  days_per_month: number | null;
  statut: string;
  drive_url: string | null;
  sales_book_url: string | null;
  calibrage_video_url: string | null;
}

interface ProspectingDay {
  id: string;
  date: string;
  sdr_first_name: string | null;
  calls_count: number | null;
  conversations_count: number | null;
  rdvs_booked: number | null;
  feedback: string | null;
  source: string;
}

interface Appointment {
  id: string;
  date: string;
  time: string;
  prospect_name: string | null;
  sales_name: string | null;
  status: string;
}

const CampagneDetailPage: React.FC = () => {
  const { client_id } = useParams<{ client_id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [client, setClient] = useState<ClientData | null>(null);
  const [editedClient, setEditedClient] = useState<Partial<ClientData>>({});
  const [prospectingDays, setProspectingDays] = useState<ProspectingDay[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [stats, setStats] = useState({
    totalRdvs: 0,
    totalCalls: 0,
    totalConversations: 0,
    conversion: 0,
    progressPercent: 0,
    daysDone: 0,
    daysPlanned: 0,
  });

  useEffect(() => {
    if (profile?.role !== 'admin' && profile?.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchCampaignData();
  }, [client_id, profile]);

  const fetchCampaignData = async () => {
    if (!client_id) return;

    try {
      setLoading(true);

      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', client_id)
        .single();

      if (clientError) throw clientError;

      setClient(clientData);
      setEditedClient(clientData);

      const { data: prospectingData, error: prospectingError } = await supabase
        .from('prospecting_days')
        .select('*')
        .eq('client_id', client_id)
        .order('date', { ascending: false });

      if (prospectingError) throw prospectingError;
      setProspectingDays(prospectingData || []);

      const { data: appointmentsData, error: appointmentsError } = await supabase
        .from('appointments')
        .select('id, date, time, prospect_name, sales_name, status')
        .eq('client_id', client_id)
        .order('date', { ascending: false });

      if (appointmentsError) throw appointmentsError;
      setAppointments(appointmentsData || []);

      const uniqueDates = new Set(prospectingData?.map(d => d.date) || []);
      const totalCalls = prospectingData?.reduce((sum, d) => sum + (d.calls_count || 0), 0) || 0;
      const totalConversations = prospectingData?.reduce((sum, d) => sum + (d.conversations_count || 0), 0) || 0;
      const totalRdvs = appointmentsData?.length || 0;

      let daysPlanned = clientData.days_per_month || 0;
      if (clientData.start_date && clientData.end_date && clientData.days_per_month) {
        const startDate = new Date(clientData.start_date);
        const endDate = new Date(clientData.end_date);
        const monthsDiff = (endDate.getTime() - startDate.getTime()) / (30 * 24 * 60 * 60 * 1000);
        daysPlanned = Math.max(Math.round(monthsDiff * clientData.days_per_month), clientData.days_per_month);
      }

      const daysDone = uniqueDates.size;
      const progressPercent = daysPlanned > 0 ? Math.round((daysDone / daysPlanned) * 100) : 0;
      const conversion = totalConversations > 0 ? Math.round((totalRdvs / totalConversations) * 100) : 0;

      setStats({
        totalRdvs,
        totalCalls,
        totalConversations,
        conversion,
        progressPercent,
        daysDone,
        daysPlanned,
      });

    } catch (error) {
      console.error('Error fetching campaign data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!client_id) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('clients')
        .update({
          start_date: editedClient.start_date,
          end_date: editedClient.end_date,
          days_per_month: editedClient.days_per_month,
          contract_type: editedClient.contract_type,
          monthly_price: editedClient.monthly_price,
          statut: editedClient.statut,
        })
        .eq('id', client_id);

      if (error) throw error;

      setClient({ ...client!, ...editedClient });
      setIsEditing(false);
      await fetchCampaignData();

    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!client_id) return;

    try {
      const { error } = await supabase
        .from('clients')
        .update({ statut: newStatus })
        .eq('id', client_id);

      if (error) throw error;

      setClient({ ...client!, statut: newStatus });
      setEditedClient({ ...editedClient, statut: newStatus });

    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Campagne non trouvée</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/campagnes')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Retour aux campagnes
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{client.name}</h1>
              <p className="text-gray-600">Paramètres et suivi de campagne</p>
            </div>

            <div className="flex items-center gap-2">
              {client.statut === 'actif' && (
                <Button
                  onClick={() => handleStatusChange('en pause')}
                  variant="secondary"
                  icon={<Pause className="h-4 w-4" />}
                >
                  Mettre en pause
                </Button>
              )}
              {client.statut === 'en pause' && (
                <Button
                  onClick={() => handleStatusChange('actif')}
                  variant="primary"
                  icon={<Play className="h-4 w-4" />}
                >
                  Relancer
                </Button>
              )}
              {client.statut !== 'inactif' && (
                <Button
                  onClick={() => handleStatusChange('inactif')}
                  variant="secondary"
                  icon={<StopCircle className="h-4 w-4" />}
                >
                  Arrêter
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card>
            <div className="flex items-center justify-between mb-2">
              <Phone className="h-8 w-8 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalCalls}</div>
            <div className="text-sm text-gray-600">Total Appels</div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-2">
              <MessageCircle className="h-8 w-8 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalConversations}</div>
            <div className="text-sm text-gray-600">Conversations</div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-2">
              <Calendar className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalRdvs}</div>
            <div className="text-sm text-gray-600">RDV Bookés</div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-8 w-8 text-amber-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.conversion}%</div>
            <div className="text-sm text-gray-600">Conversion Conv → RDV</div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="h-8 w-8 text-teal-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.daysDone} / {stats.daysPlanned}</div>
            <div className="text-sm text-gray-600">Jours faits / attendus</div>
            <div className="mt-2 bg-gray-200 rounded-full h-2">
              <div
                className="bg-teal-500 h-2 rounded-full"
                style={{ width: `${Math.min(stats.progressPercent, 100)}%` }}
              />
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Informations Client</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-600">Nom</label>
                <div className="text-gray-900 font-medium">{client.name}</div>
              </div>
              {client.email && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Mail className="h-4 w-4" />
                    {client.email}
                  </div>
                </div>
              )}
              {client.phone && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Téléphone</label>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Phone className="h-4 w-4" />
                    {client.phone}
                  </div>
                </div>
              )}
              <div className="pt-3 border-t border-gray-200 space-y-2">
                {client.sales_book_url && (
                  <a
                    href={client.sales_book_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Sales Book
                  </a>
                )}
                {client.drive_url && (
                  <a
                    href={client.drive_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Drive
                  </a>
                )}
                {client.calibrage_video_url && (
                  <a
                    href={client.calibrage_video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Calibrage
                  </a>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Paramètres Campagne</h2>
              {!isEditing && (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="secondary"
                  size="sm"
                >
                  Modifier
                </Button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Date de début</label>
                <input
                  type="date"
                  value={editedClient.start_date || ''}
                  onChange={(e) => setEditedClient({ ...editedClient, start_date: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Date de fin</label>
                <input
                  type="date"
                  value={editedClient.end_date || ''}
                  onChange={(e) => setEditedClient({ ...editedClient, end_date: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Jours par mois</label>
                <input
                  type="number"
                  value={editedClient.days_per_month || ''}
                  onChange={(e) => setEditedClient({ ...editedClient, days_per_month: parseInt(e.target.value) })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Type de contrat</label>
                <input
                  type="text"
                  value={editedClient.contract_type || ''}
                  onChange={(e) => setEditedClient({ ...editedClient, contract_type: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Prix mensuel</label>
                <input
                  type="number"
                  value={editedClient.monthly_price || ''}
                  onChange={(e) => setEditedClient({ ...editedClient, monthly_price: parseFloat(e.target.value) })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Statut</label>
                <select
                  value={editedClient.statut || ''}
                  onChange={(e) => setEditedClient({ ...editedClient, statut: e.target.value })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                >
                  <option value="actif">Actif</option>
                  <option value="en pause">En pause</option>
                  <option value="inactif">Inactif</option>
                </select>
              </div>

              {isEditing && (
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={handleSave}
                    variant="primary"
                    disabled={saving}
                    icon={<Save className="h-4 w-4" />}
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                  <Button
                    onClick={() => {
                      setEditedClient(client);
                      setIsEditing(false);
                    }}
                    variant="secondary"
                  >
                    Annuler
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>

        <Card className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Jours de Prospection</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SDR</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calls</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversations</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RDV</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {prospectingDays.map((day) => (
                  <tr key={day.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(day.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{day.sdr_first_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{day.calls_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{day.conversations_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{day.rdvs_booked || 0}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {day.source}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {prospectingDays.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Aucun jour de prospection enregistré
              </div>
            )}
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4">RDV Planifiés</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Heure</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prospect</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SDR</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {appointments.map((apt) => (
                  <tr key={apt.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(apt.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{apt.time}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{apt.prospect_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{apt.sales_name || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        apt.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {apt.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`/appointments/${apt.id}`)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Modifier
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {appointments.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Aucun RDV planifié
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CampagneDetailPage;
